import { useState, type ReactNode } from "react";
import { Button, type Column, Toast } from "@legal-500/legal500-design-system";
import { DataTable } from "@/components/DataTable/DataTable";
import LoadingState from "@/components/LoadingState";
import EmptyState from "@/components/EmptyState";
import { UnauthorizedView } from "@/components/UnauthorizedView/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import {
  customerSupportUsersService,
  type CsConsent,
  type CsWebsiteAuditEntry,
} from "@/api/customerSupportUsers.service";
import { formatDateTimeShort } from "@/utils/dateFormatter";
import sharedStyles from "../UserManagement.module.css";
import styles from "./CustomerSupport.module.css";
import PermissionGate from "@/components/Permissions/PermissionGate";
import { csReasonLabel, humaniseAccessTier, humaniseCode } from "./csLabels";
import { CsStatusChip } from "./csStatus";
import ActionModal from "./ActionModal";

// The write actions are a separate grant from the read access that opens this panel; the
// backend enforces it too, so an agent with read-only access never sees a button that 403s.
const WEBSITE_USERS_WRITE_PERMISSION = "staff.website_users.write";

type UserProfileProps = {
  userId: string;
};

/** Empty optionals read as "Not provided" (industry is expected empty for MVP users). */
function orNotProvided(value: string | null | undefined): string {
  return value && value.trim() !== "" ? value : "Not provided";
}

/** Dates guard null instead of formatting it into an "Invalid Date". */
function formatDate(value: string | null | undefined): string {
  return value ? formatDateTimeShort(value) : "—";
}

/**
 * Numbers must not go through a truthiness check: `failedLoginAttempts: 0` is a real
 * answer ("no failed attempts") and rendering it as "Not provided" would be a lie.
 */
function orNotProvidedNumber(value: number | null | undefined): string {
  return typeof value === "number" ? String(value) : "Not provided";
}

/** Booleans render explicitly — a false flag must read "No", never as a missing chip. */
function yesNo(value: boolean | null | undefined): string {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Not provided";
}

/**
 * A stored code, rendered readably. The raw code stays on the value's title so an agent
 * reconciling the panel against the database can still read it, without a second visible line
 * of clutter under every coded field.
 */
function codeValue(
  raw: string | null | undefined,
  humanise: (value: string) => string,
): { value: string; title?: string } {
  if (!raw || raw.trim() === "") return { value: orNotProvided(raw) };
  const humanised = humanise(raw);
  return { value: humanised, title: humanised === raw ? undefined : raw };
}

/** One label/value row; `children` is the value node. */
function InfoField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={sharedStyles.infoItem}>
      <span className={sharedStyles.infoLabel}>{label}</span>
      {children}
    </div>
  );
}

/** The common case: a plain text value. */
function InfoItem({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <InfoField label={label}>
      <span className={sharedStyles.infoValue} title={title}>
        {value}
      </span>
    </InfoField>
  );
}

/** Human-readable label for an audit action code. */
const AUDIT_ACTION_LABEL: Record<string, string> = {
  send_password_reset: "Password reset",
  delete_account: "Account deletion",
  cancel_deletion: "Deletion cancelled",
  view_profile: "Viewed profile",
  search: "Search",
};
const auditActionLabel = (action: string) => AUDIT_ACTION_LABEL[action] ?? action;

function consentValue(consent: CsConsent): string {
  return consent.granted ? `Granted — ${formatDate(consent.grantedAt)}` : "Not granted";
}

const AUDIT_COLUMNS: Column<CsWebsiteAuditEntry>[] = [
  {
    header: "Actor",
    accessor: "actorEmail",
    render: (value, row) => (value ? String(value) : row.actorId),
  },
  {
    header: "Action",
    accessor: "action",
    render: value => auditActionLabel(String(value)),
  },
  {
    header: "Reason",
    accessor: "reason",
    // Same vocabulary the modals file the reason under, so the log reads as the words the
    // agent chose rather than as the code they were stored as.
    render: (value, row) => {
      const label = csReasonLabel(String(value));
      return row.reasonDetail ? `${label} — ${row.reasonDetail}` : label;
    },
    wrap: true,
  },
  {
    header: "Date",
    accessor: "createdAt",
    render: value => formatDate(value as string),
  },
];

export default function UserProfile({ userId }: UserProfileProps) {
  const [activeAction, setActiveAction] = useState<"reset" | "delete" | "cancel-deletion" | null>(
    null,
  );
  const [auditExpanded, setAuditExpanded] = useState(false);
  // Success Toast host lives here so it survives the ActionModal unmounting.
  const [toast, setToast] = useState<string | null>(null);

  const {
    data: profile,
    loading,
    error,
    refetch,
  } = useQuery(() => customerSupportUsersService.getUser(userId), [userId]);

  // Lazy: `getUserAuditLog` is not called until the section is first expanded.
  const {
    data: auditData,
    loading: auditLoading,
    error: auditError,
    refetch: refetchAudit,
  } = useQuery(
    () =>
      auditExpanded
        ? customerSupportUsersService.getUserAuditLog(userId)
        : Promise.resolve<CsWebsiteAuditEntry[] | null>(null),
    [userId, auditExpanded],
  );

  const status = (error as Error & { status?: number })?.status;
  const isAuthError = status === 401 || status === 403;

  if (isAuthError) {
    return <UnauthorizedView />;
  }

  if (loading) {
    return <LoadingState />;
  }

  if (error && !isAuthError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load profile"
        message={error.message}
        action={{ label: "Retry", onClick: refetch }}
      />
    );
  }

  if (!profile) {
    return <EmptyState variant="empty" title="Profile not found" />;
  }

  const jobFunction = codeValue(profile.jobFunction, humaniseCode);

  return (
    <div className={styles.profileWrapper}>
      {toast && (
        <Toast message={toast} type="success" duration={5000} onClose={() => setToast(null)} />
      )}

      {profile.deletionScheduledAt && (
        <div className={styles.deletionBanner} role="alert">
          <strong>Pending deletion</strong> (scheduled {formatDate(profile.deletionScheduledAt)})
          {profile.deletionInitiatedActorType && (
            <span className={styles.deletionActor}>
              {" "}
              · Initiated by: {profile.deletionInitiatedActorType === "staff" ? "staff" : "user"}
            </span>
          )}
        </div>
      )}

      {/* Ordered identity → account state → organisation → place → dates → ids, so the section
          has a reading order rather than being a spray of fields an agent has to scan for one. */}
      <div className={sharedStyles.section}>
        <h2 className={sharedStyles.sectionTitle}>Details</h2>
        <div className={styles.recordGrid}>
          <InfoItem label="Email" value={profile.email} />
          <InfoItem label="First name" value={orNotProvided(profile.firstName)} />
          <InfoItem label="Last name" value={orNotProvided(profile.lastName)} />

          <InfoField label="Verification status">
            <span className={sharedStyles.infoValue}>
              <CsStatusChip status={profile.verificationStatus} />
            </span>
          </InfoField>
          <InfoItem label="Access tier" {...codeValue(profile.accessTier, humaniseAccessTier)} />
          {/* Explicit Yes/No: rendering only a "eligible" chip made ineligible users
              indistinguishable from a rendering bug. */}
          <InfoItem
            label="Legal Business eligible"
            value={yesNo(profile.isLegalBusinessEligible)}
          />

          <InfoItem label="Company" value={orNotProvided(profile.company)} />
          <InfoItem label="Company ID" value={orNotProvided(profile.companyId)} />
          <InfoItem label="Pending company ID" value={orNotProvided(profile.pendingCompanyId)} />
          <InfoItem label="Firm ref" value={orNotProvided(profile.firmRef)} />
          <InfoField label="Job function">
            <span className={sharedStyles.infoValue} title={jobFunction.title}>
              {jobFunction.value}
            </span>
            {profile.jobFunctionOther && (
              <span className={styles.subValue}>Other — {profile.jobFunctionOther}</span>
            )}
          </InfoField>
          <InfoItem label="Industry" value={orNotProvided(profile.industry)} />

          {/* Country stays the stored code: the canonical country list lives in a workspace
              package this service does not depend on. */}
          <InfoItem label="Country" value={orNotProvided(profile.country)} />
          <InfoItem label="Locale" value={orNotProvided(profile.locale)} />

          <InfoItem label="Created" value={formatDate(profile.createdAt)} />
          <InfoItem label="Last login" value={formatDate(profile.lastLoginAt)} />
          <InfoItem label="Last updated" value={formatDate(profile.updatedAt)} />

          {/* Last: an agent identifies the record by the email at the top long before they
              need its uuid. */}
          <InfoItem label="User ID" value={orNotProvided(profile.userId)} />
        </div>
      </div>

      {/* Sign-in status is grouped so "why can't this person sign in?" is one glance, not three
          fields scattered through the details grid. It shares a row with the consents: three
          fields is not a full-width card's worth, and both are short label/value records. */}
      <div className={styles.sectionPair}>
        <div className={sharedStyles.section}>
          <h2 className={sharedStyles.sectionTitle}>Sign-in status</h2>
          <div className={styles.recordList}>
            <InfoItem label="Email verified" value={formatDate(profile.emailVerifiedAt)} />
            <InfoItem
              label="Failed login attempts"
              value={orNotProvidedNumber(profile.failedLoginAttempts)}
            />
            <InfoItem label="Locked until" value={formatDate(profile.lockedUntil)} />
          </div>
        </div>

        <div className={sharedStyles.section}>
          <h2 className={sharedStyles.sectionTitle}>Consents</h2>
          <div className={styles.recordList}>
            <InfoItem label="Data sharing" value={consentValue(profile.consents.dataSharing)} />
            <InfoItem label="Marketing" value={consentValue(profile.consents.marketing)} />
            <InfoItem label="Terms & privacy" value={consentValue(profile.consents.termsPrivacy)} />
          </div>
        </div>
      </div>

      {/* Always rendered, not conditional on a pending deletion: an agent asking "was this
          account ever deleted / anonymised?" needs an explicit "—", not an absent section. */}
      <div className={sharedStyles.section}>
        <h2 className={sharedStyles.sectionTitle}>Deletion &amp; anonymisation</h2>
        <div className={styles.recordGrid}>
          <InfoItem label="Deletion scheduled" value={formatDate(profile.deletionScheduledAt)} />
          {/* No coded reason row: nothing writes `website_users.deletion_reason` — the CS action
              files its reason on the audit row (rendered in the Audit log section below) and the
              self-serve flow collects free text only, so the field read "Not provided" for every
              account and was taken for a bug. The free text below is the one that is written. */}
          <InfoItem
            label="Deletion reason detail"
            value={orNotProvided(profile.deletionReasonText)}
          />
          <InfoItem
            label="Deletion initiated by (actor type)"
            {...codeValue(profile.deletionInitiatedActorType, humaniseCode)}
          />
          <InfoItem
            label="Deletion initiated by (actor)"
            value={orNotProvided(profile.deletionInitiatedBy)}
          />
          <InfoItem label="Anonymised" value={formatDate(profile.anonymisedAt)} />
        </div>
      </div>

      <div className={sharedStyles.section}>
        <h2 className={sharedStyles.sectionTitle}>Actions</h2>
        <PermissionGate
          permission={WEBSITE_USERS_WRITE_PERMISSION}
          fallback={
            <p className={styles.subValue}>
              You have read-only access. Contact an administrator if you need to make changes.
            </p>
          }
        >
          <div className={styles.actionsRow}>
            <Button variant="outlined" color="primary" onClick={() => setActiveAction("reset")}>
              Send password reset
            </Button>
            <Button variant="outlined" color="error" onClick={() => setActiveAction("delete")}>
              Delete account + data
            </Button>
            {/* Only offered while there is a live pending deletion to reverse: once the record
                is anonymised the purge has run and there is nothing left to restore. */}
            {profile.deletionScheduledAt !== null && profile.anonymisedAt === null && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setActiveAction("cancel-deletion")}
              >
                Cancel scheduled deletion
              </Button>
            )}
          </div>
        </PermissionGate>
        {activeAction && (
          <ActionModal
            action={activeAction}
            userId={userId}
            onClose={() => setActiveAction(null)}
            onSuccess={msg => {
              setToast(msg);
              setActiveAction(null);
              refetch();
              refetchAudit();
            }}
            onStateRefresh={() => {
              refetch();
              refetchAudit();
            }}
          />
        )}
      </div>

      <div className={sharedStyles.section}>
        <button
          type="button"
          className={styles.auditToggle}
          aria-expanded={auditExpanded}
          onClick={() => setAuditExpanded(expanded => !expanded)}
        >
          {auditExpanded ? "▾" : "▸"} Audit log
        </button>

        {auditExpanded && (
          <div className={styles.auditSection}>
            {auditLoading ? (
              <LoadingState />
            ) : auditError ? (
              <EmptyState
                variant="error"
                title="Couldn't load audit log"
                message={auditError.message}
                action={{ label: "Retry", onClick: refetchAudit }}
              />
            ) : !auditData || auditData.length === 0 ? (
              <EmptyState variant="empty" title="No audit entries" />
            ) : (
              <DataTable data={auditData} columns={AUDIT_COLUMNS} rowKey="id" size="small" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
